import { apiFetch, apiGet, apiUploadWithProgress, API_BASE } from './api';

/**
 * THE ONE UPLOAD / DELETE / REFRESH PATH FOR ORDER ATTACHMENTS, extracted
 * 2026-09-13 when the inline editor gained a PER-LINE-ITEM attach control
 * (owner: *"Yes attachments per item is needed"*, asked whether it had to
 * exist before `/orders/:id` is retired).
 *
 * ── Why an extraction rather than a second copy ───────────────────────────
 * `components/OrderExpandedDetails.js` already owned an immediate upload, an
 * immediate delete and the detail re-fetch that follows either. The new
 * control needs the SAME three against the SAME endpoints from a DIFFERENT
 * component (`components/OrderInlineEditor.js`, which renders the form the
 * panel sits beneath). Copying them would put two delete paths on one screen
 * — a second `window.confirm` wording, a second error shape, a second thing
 * to remember when `DELETE /api/orders/:id/files/:fileId` changes. CLAUDE.md's
 * Code Unification Principle, and the panel's own docblock, both say extract.
 *
 * 🔑 **THESE FUNCTIONS DO NO UI AND HOLD NO STATE.** They throw an `Error`
 * carrying THE SERVER'S OWN MESSAGE; the caller owns the busy flag, the
 * banner-or-toast and the refresh it wants. That is what lets the panel keep
 * its inline `message` banner while the inline editor uses the app toast,
 * without either of them re-implementing the request.
 *
 * 🚨 **THE FILE-TYPE POLICY IS THE SERVER'S AND MUST NOT BE MIRRORED HERE.**
 * `server/CLAUDE.md` → *Upload file-type policy*: order files are a
 * *general-purpose* surface on the shared BLOCKLIST (`createUploader({
 * blockedExt })`), never an allowlist. A client-side whitelist would drift
 * from `server/helpers/uploads.js` the first time the owner rules on an
 * extension — so nothing here inspects a file name. The server refuses and
 * its message is surfaced verbatim.
 */

/** The ONE wording for the destructive confirm — shared so two surfaces cannot ask differently. */
export const REMOVE_ATTACHMENT_CONFIRM = 'Remove this attachment? This cannot be undone.';

/**
 * Is this `order_files` row a LINE-ITEM attachment?
 *
 * 🪤 `!= null`, NOT `!f.order_item_id` — `components/OrderExpandedDetails.js`'s
 * own three-way split records why: *"a falsy value that is also a legitimate
 * answer can never double as the absence marker"*. No id is 0 on either engine,
 * so nothing is broken by the falsy form; what it costs is that two surfaces
 * splitting the same array disagree about the test. Hence one function.
 *
 * 🔑 DELIVERY WINS THE OVERLAP, matching that panel's ordering. The overlap is
 * unreachable — `POST /:id/items/:itemId/files` omits `category` (so the row
 * takes the DB default `'attachment'`) and `POST /:id/files` hard-codes
 * `order_item_id = null` — but the two surfaces must still answer alike.
 */
export function isItemAttachment(file) {
  return !!file && file.category !== 'delivery' && file.order_item_id != null;
}

/** `order.files` grouped by `order_item_id`, item attachments only. */
export function groupItemAttachmentsByItemId(files) {
  const map = {};
  (Array.isArray(files) ? files : []).forEach((f) => {
    if (!isItemAttachment(f)) return;
    if (!map[f.order_item_id]) map[f.order_item_id] = [];
    map[f.order_item_id].push(f);
  });
  return map;
}

/**
 * POST files to an order, or to one of its line items.
 *
 * 🪤 `itemId` selects a DIFFERENT ROUTE, not a different body. `POST
 * /api/orders/:id/files` hard-codes `order_item_id = null`, so an item file
 * can only be created through `POST /api/orders/:id/items/:itemId/files` —
 * the read-then-write asymmetry the expanded panel's Attachments comment
 * records. `category` is only meaningful on the order-level route.
 *
 * 🔀 THROUGH `apiUploadWithProgress` SINCE 2026-09-14, so the row can draw a bar:
 * `onProgress(fraction)` is the request's monotonic 0…1 (`hooks/useUploadProgress.js`
 * spreads it across the files). Same session cookie, department header, 401
 * redirect and `{ error }` as `apiFetch` — see that transport's docblock.
 *
 * @returns {Promise<Array>} the `files` array the route echoes back.
 */
export async function uploadOrderFiles(orderId, files, { category = 'attachment', itemId = null, onProgress = null } = {}) {
  const list = Array.from(files || []);
  if (!orderId || list.length === 0) return [];
  const formData = new FormData();
  list.forEach(f => formData.append('files', f));
  const url = itemId != null
    ? `${API_BASE}/api/orders/${orderId}/items/${itemId}/files`
    : `${API_BASE}/api/orders/${orderId}/files${category === 'delivery' ? '?category=delivery' : ''}`;
  const res = await apiUploadWithProgress(url, formData, { onProgress });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to upload attachment(s)');
  }
  const data = await res.json().catch(() => ({}));
  return data.files || [];
}

/**
 * The saved `order_items.id` a FORM line became after a POST/PUT — its own `id`
 * if it had one, else the response row carrying its `client_key` (echoed by the
 * server on INSERTED rows only). `null` when neither answers.
 *
 * 🚨 **NEVER POSITIONAL (2026-09-14).** `pages/OrderPage.js` used
 * `putResponse.items[formIndex]`, but `buildPayload` drops a blank-description
 * line before the request, so one blank line above an attachment shifted every
 * later file onto the NEXT line's id. A `null` here is refused by the callers —
 * an unfiled attachment is reported, a misfiled one is silent.
 */
export function savedItemIdFor(formItem, savedItems) {
  if (!formItem) return null;
  if (formItem.id != null) return formItem.id;
  if (!formItem.client_key) return null;
  const row = (Array.isArray(savedItems) ? savedItems : []).find((r) => r && r.client_key === formItem.client_key);
  return row && row.id != null ? row.id : null;
}

// 🔻 `flushStagedItemFiles` / `describeStagedUploadFailures` (2026-09-14, the
// inline editor's post-PUT upload of browser-staged files) were DELETED 2026-09-15:
// files upload at pick now and the save CLAIMS them — see below.

// ── PENDING UPLOADS (R13, 2026-09-15) ─────────────────────────────────────
// Owner: *"upload the file straight away, delete it if the user does not save
// the line or they delete the line"*. A file picked where it has nowhere to go
// yet (a new line, New Order, the full order page's edit mode) is uploaded AT
// ONCE to the caller's pending store and CLAIMED onto the order when the save
// lands. The state (which files, which line, uploading/uploaded/failed) is
// `hooks/usePendingOrderUploads.js`; these are its three requests. Server half:
// `server/helpers/pendingOrderUploads.js`.

/**
 * Upload ONE file to the caller's pending store under a client-minted key.
 * Throws the server's message; an abort rethrows the `AbortError`.
 * @returns {Promise<{ key, file_name, file_size, file_type }>}
 */
export async function uploadPendingOrderFile(key, file, { onProgress = null, signal = null } = {}) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiUploadWithProgress(
    `${API_BASE}/api/order-uploads/pending?key=${encodeURIComponent(key)}`,
    formData,
    { onProgress, signal }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to upload attachment');
  }
  return res.json().catch(() => ({ key }));
}

/**
 * Keys per discard request. The server accepts far more
 * (`PENDING_UPLOAD_LIMITS.maxDiscardKeys`, 500); chunking at the per-user pending
 * cap keeps every request small and means no discard is ever REFUSED for its
 * size (review I1, 2026-09-15 — a single over-cap request 400'd, the refusal was
 * swallowed here, and the caller stayed locked out of new picks for 24h).
 */
export const PENDING_DISCARD_CHUNK = 25;

/**
 * Tell the server to delete pending uploads, in chunks of `PENDING_DISCARD_CHUNK`.
 * BEST EFFORT and NEVER throws — the 24h sweep is the backstop. `keepalive` lets
 * it survive a page hide. Resolves `true` only when every chunk was accepted.
 */
export async function discardPendingOrderUploads(keys, { keepalive = false } = {}) {
  const list = Array.from(keys || []);
  if (list.length === 0) return false;
  const sends = [];
  for (let i = 0; i < list.length; i += PENDING_DISCARD_CHUNK) {
    const chunk = list.slice(i, i + PENDING_DISCARD_CHUNK);
    sends.push(apiFetch(`${API_BASE}/api/order-uploads/pending/discard`, {
      method: 'POST',
      body: JSON.stringify({ keys: chunk }),
      keepalive,
    }).then((res) => !!(res && res.ok)).catch(() => false));
  }
  const results = await Promise.all(sends);
  return results.every(Boolean);
}

/**
 * Claim pending uploads onto a saved order — onto one LINE when `itemId` is
 * given, else as order-level attachments. `idempotencyKey` is this claim
 * ATTEMPT's identity (reuse it on a retry), so a lost response replays rather
 * than answering "not found". Throws the server's message.
 * @returns {Promise<Array>} the `order_files` rows created.
 */
export async function claimPendingOrderUploads(orderId, keys, { itemId = null, idempotencyKey = null } = {}) {
  const url = itemId != null
    ? `${API_BASE}/api/orders/${orderId}/items/${itemId}/files/claim`
    : `${API_BASE}/api/orders/${orderId}/files/claim`;
  const res = await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify({ keys: Array.from(keys || []) }),
    ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || 'Failed to attach the uploaded files');
    // `pending_upload_not_found` is how the hook tells an EXPIRED file from a refusal.
    if (body.code) err.code = body.code;
    err.status = res.status;
    throw err;
  }
  const data = await res.json().catch(() => ({}));
  return data.files || [];
}

/**
 * One sentence naming every file a save could not attach, with the server's words.
 * 🪤 It points at the row's Retry (re-check minor, 2026-09-15): it said "Save again
 * to retry.", and for an EXPIRED file saving again only refuses — the file has to
 * be re-uploaded (Retry) or removed first. For a refused claim, Save still works.
 */
export function describePendingClaimFailures(failures) {
  const names = failures.flatMap((f) => f.names);
  const messages = [...new Set(failures.map((f) => f.message))];
  const noun = names.length === 1 ? 'attachment' : 'attachments';
  return `Order saved, but ${names.length} ${noun} did not attach: ${names.join(', ')} — ${messages.join('; ')}. `
    + 'If a file shows Retry, press it (or remove the file), then save again.';
}

/**
 * Ask the destructive question, in the ONE wording. Returns `false` when the
 * user declined — which is "nothing happened", never a failure.
 *
 * 🔀 **SPLIT OUT OF A COMBINED `confirmAndDeleteOrderFile` ON 2026-09-13
 * (review F4), AND THE SPLIT IS THE FIX RATHER THAN TIDYING.** Combined, a
 * caller could only set its busy flag and clear its banner BEFORE the call —
 * i.e. before the question was asked — so **declining wiped the previous
 * success/error banner**, which the pre-extraction code did not do. Setting
 * them AFTER the combined call is not the repair either: the DELETE has already
 * finished by then, so "busy" would cover nothing. The caller needs the seam,
 * so the seam exists.
 *
 * 🔑 The wording still lives HERE, asked once, so two surfaces cannot ask
 * differently.
 */
export function confirmRemoveAttachment() {
  // eslint-disable-next-line no-alert
  return window.confirm(REMOVE_ATTACHMENT_CONFIRM);
}

/**
 * DELETE one `order_files` row. Throws with the server's own message.
 *
 * `DELETE /api/orders/:id/files/:fileId` decides PER FILE server-side;
 * mirroring that is the CALLER's `canRemoveFile` predicate, which is why this
 * function takes no authority argument.
 */
export async function deleteOrderFile(orderId, fileId) {
  if (!orderId || fileId == null) return false;
  const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/files/${fileId}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to remove attachment');
  }
  return true;
}

/**
 * Re-read `GET /api/orders/:id` and hand the fresh detail to the page.
 *
 * 🚨 **THE FILE LIST IS A SLICE OF THE DETAIL CACHE, SO EVERY MUTATION HAS TO
 * COME BACK THROUGH THE PAGE.** A component that kept its own copy of `files`
 * is how the cached `can_edit` went stale in the first place
 * (`components/OrderInlineEditor.js`'s docblock). Silent when the caller
 * supplies no `onFilesChanged`: a surface with nowhere to put the answer must
 * not issue the request.
 *
 * 🚨 **`saveEpochRef` IS NOT OPTIONAL POLISH ON A SURFACE BESIDE A SAVE —
 * review F1, 2026-09-13, reproduced.** The read and the write are two requests
 * and the response can land LAST:
 *   pick a file → POST resolves → this GET is issued → the user clicks Save →
 *   `PUT /api/orders/:id` → `onSaved` → the page deletes its cache entry and
 *   `refreshOneOrder` writes FRESH detail → **this GET, issued PRE-PUT, lands
 *   and overwrites the cache with PRE-PUT data.** `toggleExpand` then serves
 *   that on re-expand WITHOUT a fetch, so the row shows the values the user
 *   just replaced — the house's "the app says it saved and it did not" class —
 *   and a second save can write the old values back.
 *
 * 🔑 **A `useLatestRequest` SEQUENCE ALONE DOES NOT FIX IT, AND REACHING FOR
 * ONE IS THE PLAUSIBLE WRONG ANSWER.** That hook orders a surface's own
 * requests against each other; the competing writer here is the PAGE's
 * post-save refresh, which no sequence counter of ours can see. The question is
 * "has a newer, more authoritative writer settled since I started", and the
 * counter that answers it is bumped by `components/OrderInlineEditor.js` on
 * every successful save and shared with the panel by
 * `components/OrderExpandedRow.js`.
 *
 * 🪤 **THE CAPTURE-AND-COMPARE LIVES HERE, NOT AT THE CALL SITES, and that is a
 * `check-duplication` ruling rather than taste.** This started as an `isStale`
 * PREDICATE each caller built for itself; both callers then wrote the same four
 * lines and the gate scored them 1.00 over 27 tokens and BLOCKED the commit —
 * correctly. Taking the ref instead makes the guard one implementation that
 * cannot drift, and there is no second shape asking for a general predicate.
 *
 * ⚠️ Compared after the `await`, never before: reading it at issue time asks a
 * question whose answer cannot yet have changed.
 */
export async function refreshOrderFiles(orderId, onFilesChanged, { saveEpochRef = null } = {}) {
  if (!onFilesChanged || !orderId) return;
  const epochAtIssue = saveEpochRef ? saveEpochRef.current : null;
  const res = await apiGet(`${API_BASE}/api/orders/${orderId}`);
  if (!res.ok) return;
  const fresh = await res.json();
  if (saveEpochRef && saveEpochRef.current !== epochAtIssue) return;
  onFilesChanged(orderId, fresh);
}
